import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Table } from 'reactstrap';

import apiOffers from 'api/offers';
import ISearchedPosition from 'api/interfaces/offers/ISearchedPosition';
import { formatServerDate } from 'services/dates/serverTime';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { NDA_MASKED_VALUE } from 'services/enums/offers/ndaProtections';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import { useCurrency } from 'services/offers/currency';
import translations from 'services/translations/translations';

import DepartmentSelect from '../../../DepartmentSelect';

import {
  getReferencePrices,
  getReferenceRows,
  IReferenceFigures,
  IReferenceRow,
} from './getReferencePrices';

import 'style/offers/ReferencePrices.css';

// The periods the user can choose, in months; the last month by default
const PERIODS = [1, 3, 6, 12, 24];

// In the time of the browser: the server sends UTC
const formatDate = (dateTime: string): string => formatServerDate(dateTime);

/**
 * Reference prices of the position: the best and the worst prices of the positions of
 * other offers of the same type (e.g. preliminary offers) named exactly the same way, and
 * their median. Shown under the internal memo of the calculation window.
 */
const ReferencePrices = ({
  name,
  offerProductId,
  offerType,
  unit,
}: {
  name: string;
  offerProductId?: number;
  // Only offers of the type are searched, e.g. preliminary-offer
  offerType?: string;
  unit: string;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers, translations.common]);
  const currency = useCurrency();
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.calculationWindow}:referencePrices:${key}`, options);

  const [months, setMonths] = useState(PERIODS[0]);
  const [positions, setPositions] = useState<ISearchedPosition[] | null>(null);
  // The offers of one department, or of all
  const [departmentId, setDepartmentId] = useState('');

  const trimmedName = name.trim();

  // Searched again as the name of the position is typed or the period is changed
  useEffect(() => {
    if (trimmedName === '') {
      setPositions([]);
      return undefined;
    }

    let cancelled = false;
    setPositions(null);

    const timeout = setTimeout(async () => {
      try {
        const found = await apiOffers.positionSearch.get({
          excludeOfferProductId: offerProductId,
          months,
          name: trimmedName,
          organizationUnitId: departmentId ? Number(departmentId) : undefined,
          type: offerType,
        });
        if (!cancelled) {
          setPositions(found);
        }
      } catch (error) {
        if (!cancelled) {
          setPositions([]);
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [departmentId, months, offerProductId, offerType, trimmedName]);

  const prices = useMemo(
    () => getReferencePrices(getReferenceRows(positions ?? [], unit)),
    [positions, unit]
  );

  const unitText = unit ? t(`${translations.common}:units:${unit}`) : '';
  const perUnit = unitText ? `${currency}/${unitText}` : currency;

  const renderFigures = (figures: IReferenceFigures) => (
    <>
      <td className="number">
        {formatGroupedNumber(figures.quantity)} {unitText}
      </td>
      <td className="number key-figure">
        {formatGroupedNumber(figures.sellingPrice)} {perUnit}
      </td>
      <td className="number">
        {formatGroupedNumber(figures.expense)} {perUnit}
      </td>
      <td className="number">
        {formatGroupedNumber(figures.contributionMargin)} {perUnit}
      </td>
      <td className="number key-figure">
        {formatGroupedNumber(figures.contributionMarginRatio)} %
      </td>
    </>
  );

  const renderRows = (rows: IReferenceRow[], title: string) => (
    <>
      <tr className="reference-group">
        <th colSpan={9}>{title}</th>
      </tr>
      {rows.map((row) => (
        <tr key={row.offerProductId} title={row.targetName}>
          <td>{formatDate(row.offerDate)}</td>
          {renderFigures(row)}
          <td className="reference-status">
            {getOfferStatusLabel(t, row.offerStatus, row.offerType)}
          </td>
          <td className="reference-customer">
            {row.customerName === NDA_MASKED_VALUE
              ? t(`${translations.offers}:nda.Protected`)
              : row.customerName ?? ''}
          </td>
          <td className="organization-unit">{row.organizationUnit ?? ''}</td>
        </tr>
      ))}
    </>
  );

  const renderContent = () => {
    if (trimmedName === '') {
      return <p className="reference-info">{text('No name')}</p>;
    }

    if (positions === null) {
      return <p className="reference-info">{text('Loading')}</p>;
    }

    if (prices.median === null) {
      return <p className="reference-info">{text('None found')}</p>;
    }

    return (
      <Table size="sm" className="reference-table">
        <thead>
          <tr>
            <th>{text('Date')}</th>
            <th className="number">{text('Quantity')}</th>
            <th className="number">{text('Selling price')}</th>
            <th className="number">{text('Expenses')}</th>
            <th className="number">{text('Margin')}</th>
            <th className="number">{text('Margin %')}</th>
            <th>{t(`${translations.offers}:Status`)}</th>
            <th>{t(`${translations.offers}:Customer Office`)}</th>
            <th>{text('Organization unit')}</th>
          </tr>
        </thead>
        <tbody>
          {renderRows(prices.best, text('Best', { count: prices.best.length }))}
          <tr className="reference-median">
            <th>{text('Median', { count: prices.count })}</th>
            {renderFigures(prices.median)}
            <td colSpan={3} />
          </tr>
          {prices.worst.length === 0
            ? null
            : renderRows(
                [...prices.worst].reverse(),
                text('Worst', { count: prices.worst.length })
              )}
        </tbody>
      </Table>
    );
  };

  return (
    <div className="reference-prices">
      <div className="reference-header">
        <p className="version-details-title">{text('Title')}</p>
        <Input
          aria-label={text('Period')}
          bsSize="sm"
          className="reference-period"
          onChange={(event) => setMonths(Number(event.target.value))}
          type="select"
          value={months}
        >
          {PERIODS.map((period) => (
            <option key={period} value={period}>
              {text('Months', { count: period })}
            </option>
          ))}
        </Input>
        <DepartmentSelect
          bsSize="sm"
          className="reference-period"
          onChange={setDepartmentId}
          value={departmentId}
        />
      </div>
      <p className="reference-info">
        {text('Info', {
          name: trimmedName || '–',
          type: offerType ? t(`${translations.offers}:types:${offerType}`) : '',
        })}
      </p>
      {renderContent()}
    </div>
  );
};

export default ReferencePrices;
